/* eslint-disable better-mutation/no-mutation */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type {
    RefundCheckoutPurchaseOrder,
    RefundCheckoutSlice,
    RefundOngoingDiscount,
    RefundRefundState,
} from 'tradera/apps/my-tradera/refund-checkout/state/types';
import { REFUND_STEP } from 'tradera/apps/my-tradera/refund-checkout/state/types';
import type { DiscountPaymentOptionApiDto } from 'tradera/state/services/types/webapi-mytradera-generated';

const refundCheckoutSlice = createSlice({
    name: 'refundCheckout',
    initialState: {
        currentStep: REFUND_STEP.LOADING,
        refund: { canEdit: true },
        payment: { modal: {} },
    } as RefundCheckoutSlice,
    reducers: {
        setPurchaseOrder: (
            state,
            { payload }: PayloadAction<RefundCheckoutPurchaseOrder>,
        ) => {
            state.purchaseOrder = payload;
            state.currentStep = REFUND_STEP.START;
        },
        setPaymentOptions: (
            state,
            { payload }: PayloadAction<Array<DiscountPaymentOptionApiDto>>,
        ) => {
            state.payment.paymentOptions = payload;
        },
        setSelectedPayment: (state, { payload }) => {
            state.payment.selectedPayment = payload;
        },
        setPaymentMethodData: (state, { payload }) => {
            state.payment.paymentMethodData = payload;
        },
        setPaymentMethodValid: (state, { payload }: PayloadAction<boolean>) => {
            state.payment.isValidPaymentMethod = payload;
        },
        setRefundIsSaving: (state, { payload }: PayloadAction<boolean>) => {
            state.refund.isSaving = payload;
            state.refund.canEdit = !payload;
        },
        setRefundError: (
            state,
            { payload }: PayloadAction<string | null | undefined>,
        ) => {
            state.refund = {
                ...state.refund,
                ...{
                    error: payload,
                    isSaving: false,
                    canEdit: true,
                    discountId: undefined,
                    discountAttemptId: undefined,
                },
            };
            state.ongoingDiscount = undefined;
        },
        setPaymentError: (
            state,
            { payload }: PayloadAction<string | null | undefined>,
        ) => {
            state.payment.error = payload;
        },
        openPaymentModal: (state, { payload }: PayloadAction<string>) => {
            state.payment.modal = {
                open: payload,
            };
        },
        closePaymentModal: (state) => {
            state.payment.modal = {
                open: '',
            };
        },
        setDiscountId: (
            state,
            { payload }: PayloadAction<number | undefined>,
        ) => {
            state.refund.discountId = payload;
        },
        setCanEdit: (state, { payload }: PayloadAction<boolean>) => {
            state.refund.canEdit = payload;
        },
        setDiscountPaymentAttemptId: (
            state,
            { payload }: PayloadAction<string | undefined>,
        ) => {
            state.payment.discountPaymentAttemptId = payload;
        },
        setDiscountAttemptId: (
            state,
            { payload }: PayloadAction<string | undefined>,
        ) => {
            state.refund.discountAttemptId = payload;
        },
        setOngoingDiscount: (
            state,
            { payload }: PayloadAction<RefundOngoingDiscount | undefined>,
        ) => {
            state.ongoingDiscount = payload;
        },
        setCheckoutStep: (state, { payload }: PayloadAction<REFUND_STEP>) => {
            state.currentStep = payload;
        },
        setRefundState: (
            state,
            { payload }: PayloadAction<Partial<RefundRefundState>>,
        ) => {
            state.refund = { ...state.refund, ...payload };
        },
        setIsPaying: (state, { payload }: PayloadAction<boolean>) => {
            state.payment.isPaying = payload;
        },
        setIsCancelling: (state, { payload }: PayloadAction<boolean>) => {
            state.payment.isCancelling = payload;
        },
    },
});

export const {
    setPurchaseOrder,
    setPaymentOptions,
    setSelectedPayment,
    setPaymentMethodData,
    setPaymentMethodValid,
    setRefundIsSaving,
    setRefundError,
    setPaymentError,
    openPaymentModal,
    closePaymentModal,
    setDiscountId,
    setCanEdit,
    setDiscountPaymentAttemptId,
    setDiscountAttemptId,
    setOngoingDiscount,
    setCheckoutStep,
    setRefundState,
    setIsPaying,
    setIsCancelling,
} = refundCheckoutSlice.actions;

export default refundCheckoutSlice.reducer;
